"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * 2026-08-29 flow pass — two small primitives that make navigating the
 * public pages feel current instead of static:
 *
 *  - `PageEntrance`: a soft fade-rise the moment a page mounts, so route
 *    changes read as a transition, not a repaint. Reduced-motion users
 *    get an instant render.
 *  - `ReadingProgress`: a thin lime progress bar pinned to the top of
 *    long-form pages (guides), filling as the reader scrolls — the
 *    standard modern affordance for "how much is left".
 */

import { motion, useReducedMotion, useScroll, useSpring } from "framer-motion"
import type { PropsWithChildren } from "react"

/** `skip`: the content is already on screen (a prerendered page the app took over) — fading it in again would flash it. */
export function PageEntrance({ children, skip = false }: PropsWithChildren<{ skip?: boolean }>) {
  const reduceMotion = useReducedMotion()
  if (reduceMotion || skip) return <>{children}</>
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  )
}

export function ReadingProgress() {
  const { scrollYProgress } = useScroll()
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 28, restDelta: 0.001 })
  return (
    <motion.div
      aria-hidden="true"
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-50 h-1 origin-left bg-lime"
    />
  )
}
